import { useState, useEffect, useCallback } from 'react'
import type { Category, Account } from '@zeni/shared'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import type { TranslationKey } from '../i18n/translations'
import { recurringApi, categoriesApi, accountsApi } from '../lib/api'
import { CategoryCombobox } from '../components/CategoryCombobox'
import { btnAccentSm, btnIconDanger, btnPrimary, btnSecondary, modalTitle, pageSubtitle, pageTitle, typeToggleGroup, typeToggleItem } from '../lib/ui'

interface RecurringItem {
  id: string
  accountId: string
  accountName: string
  type: string
  categoryId: string
  categoryName: string
  amount: number
  description: string
  note?: string
  pattern: string
  patternLabel: string
  dayOfMonth?: number
  dayOfWeek?: number
  monthOfYear?: number
  reminderEnabled: boolean
  endDate?: string
  isArchived: boolean
  nextDueDate: string
  createdAt: string
  updatedAt: string
}

interface PendingItem {
  id: string
  description: string
  amount: number
  dueDate: string
  isPastDue: boolean
}

const DAY_OF_WEEK_KEYS: TranslationKey[] = [
  'common_day_sun',
  'common_day_mon',
  'common_day_tue',
  'common_day_wed',
  'common_day_thu',
  'common_day_fri',
  'common_day_sat',
]

const PATTERN_OPTIONS: { value: string; labelKey: TranslationKey }[] = [
  { value: 'monthly_fixed', labelKey: 'recurring_pattern_monthly_fixed' },
  { value: 'monthly_last', labelKey: 'recurring_pattern_monthly_last' },
  { value: 'weekly', labelKey: 'recurring_pattern_weekly' },
  { value: 'yearly', labelKey: 'recurring_pattern_yearly' },
]

export default function Recurring() {
  const { accessToken } = useAuth()
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const token = accessToken ?? ''

  const [items, setItems] = useState<RecurringItem[]>([])
  const [pending, setPending] = useState<PendingItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const [categories, setCategories] = useState<Category[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])

  const [modalOpen, setModalOpen] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [modalSaving, setModalSaving] = useState(false)

  const [newDescription, setNewDescription] = useState('')
  const [newType, setNewType] = useState<'income' | 'expense'>('expense')
  const [newAmount, setNewAmount] = useState('')
  const [newCategoryId, setNewCategoryId] = useState('')
  const [newAccountId, setNewAccountId] = useState('')
  const [newPattern, setNewPattern] = useState('monthly_fixed')
  const [newDayOfMonth, setNewDayOfMonth] = useState('1')
  const [newDayOfWeek, setNewDayOfWeek] = useState('0')
  const [newMonthOfYear, setNewMonthOfYear] = useState('1')
  const [newReminderEnabled, setNewReminderEnabled] = useState(false)
  const [newExcluded, setNewExcluded] = useState(false)

  const fetchData = useCallback(() => {
    if (!token) return
    setLoading(true)
    setError(null)
    Promise.all([
      recurringApi.list(token),
      recurringApi.pending(token),
    ])
      .then(([listRes, pendingRes]) => {
        setItems(listRes.data)
        setPending(pendingRes.data)
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setLoading(false))
  }, [token, t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (!token) return
    Promise.all([
      categoriesApi.list(token),
      accountsApi.list(token),
    ])
      .then(([catRes, accRes]) => {
        setCategories(catRes.data)
        setAccounts(accRes.data)
      })
      .catch(() => undefined)
  }, [token])

  const handleGenerate = async (id: string) => {
    try {
      await recurringApi.generate(token, id)
      setSuccessMsg(t('recurring_generate_success'))
      fetchData()
      setTimeout(() => setSuccessMsg(null), 3000)
    } catch (err: unknown) {
      setSuccessMsg(err instanceof Error ? err.message : t('recurring_generate_error'))
      setTimeout(() => setSuccessMsg(null), 3000)
    }
  }

  const handleArchive = async (id: string) => {
    if (!confirm(t('recurring_archive_confirm'))) return
    try {
      await recurringApi.archive(token, id)
      fetchData()
    } catch {
      // ignore
    }
  }

  const handleModalOpen = () => {
    setNewDescription('')
    setNewType('expense')
    setNewAmount('')
    setNewCategoryId(categories[0]?.id ?? '')
    setNewAccountId(accounts[0]?.id ?? '')
    setNewPattern('monthly_fixed')
    setNewDayOfMonth('1')
    setNewDayOfWeek('0')
    setNewMonthOfYear('1')
    setNewReminderEnabled(false)
    setNewExcluded(false)
    setModalError(null)
    setModalOpen(true)
  }

  const handleModalSave = async () => {
    const amount = parseInt(newAmount, 10)
    if (!newDescription.trim() || isNaN(amount) || amount <= 0 || !newCategoryId || !newAccountId) {
      setModalError(t('recurring_modal_invalid'))
      return
    }
    setModalSaving(true)
    setModalError(null)
    try {
      await recurringApi.create(token, {
        accountId: newAccountId,
        type: newType,
        categoryId: newCategoryId,
        amount,
        description: newDescription.trim(),
        pattern: newPattern,
        dayOfMonth: (newPattern === 'monthly_fixed' || newPattern === 'yearly') ? parseInt(newDayOfMonth, 10) : undefined,
        dayOfWeek: newPattern === 'weekly' ? parseInt(newDayOfWeek, 10) : undefined,
        monthOfYear: newPattern === 'yearly' ? parseInt(newMonthOfYear, 10) : undefined,
        reminderEnabled: newReminderEnabled,
        excluded: newExcluded,
      })
      setModalOpen(false)
      fetchData()
    } catch (err: unknown) {
      setModalError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setModalSaving(false)
    }
  }

  const pendingList = pending
    .map((p) =>
      t('recurring_pending_item')
        .replace('{description}', p.description)
        .replace('{date}', p.dueDate),
    )
    .join(t('recurring_pending_separator'))

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className={pageTitle}>{t('recurring_title')}</h1>
          <p className={pageSubtitle}>{t('recurring_subtitle')}</p>
        </div>
        <button
          onClick={handleModalOpen}
          className={`${btnPrimary} self-start sm:self-auto`}
        >
          {t('recurring_add')}
        </button>
      </div>

      {pending.length > 0 && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 dark:bg-yellow-950 dark:border-yellow-800">
          <p className="text-sm text-yellow-700 font-medium dark:text-yellow-400">
            {t('recurring_pending_label').replace('{count}', String(pending.length))}&nbsp;
            <span className="font-normal">{pendingList}</span>
          </p>
        </div>
      )}

      {successMsg && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 dark:bg-green-950 dark:border-green-800">
          <p className="text-sm text-green-700 dark:text-green-400">{successMsg}</p>
        </div>
      )}

      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto dark:bg-gray-900 dark:border-gray-800">
        {loading ? (
          <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('common_loading')}</div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 text-sm dark:text-red-400">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('recurring_empty')}</div>
        ) : (
          <table className="w-full min-w-[720px]">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('recurring_col_name')}</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('recurring_col_amount')}</th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('recurring_col_category')}</th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('recurring_col_pattern')}</th>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('recurring_col_next')}</th>
                <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('recurring_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`border-t border-gray-100 dark:border-gray-800 ${item.isArchived ? 'bg-gray-50 opacity-60 dark:bg-gray-800' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100">
                    {item.description}
                    {item.isArchived && (
                      <span className="ml-2 text-xs text-gray-400 border border-gray-300 rounded px-1 dark:text-gray-500 dark:border-gray-600">{t('recurring_archived_badge')}</span>
                    )}
                  </td>
                  <td className={`px-3 py-1.5 text-sm font-medium text-right ${item.type === 'expense' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {item.type === 'expense' ? '-' : '+'}{formatJPY(item.amount)}
                  </td>
                  <td className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">{item.categoryName}</td>
                  <td className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">{item.patternLabel}</td>
                  <td className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">{item.nextDueDate}</td>
                  <td className="px-3 py-1.5 text-center">
                    {!item.isArchived && (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => { void handleGenerate(item.id) }}
                          className={btnAccentSm}
                        >
                          {t('recurring_register_today')}
                        </button>
                        <button
                          onClick={() => { void handleArchive(item.id) }}
                          className={`${btnIconDanger} text-base`}
                          title={t('recurring_archive_title')}
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 max-h-[90vh] overflow-y-auto dark:bg-gray-900">
            <h2 className={`${modalTitle} mb-4`}>{t('recurring_modal_title')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_name')}</label>
                <input
                  type="text"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder={t('recurring_form_name_placeholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_type')}</label>
                <div className={typeToggleGroup}>
                  <button
                    onClick={() => setNewType('expense')}
                    className={typeToggleItem('expense', newType === 'expense')}
                  >
                    {t('type_expense')}
                  </button>
                  <button
                    onClick={() => setNewType('income')}
                    className={typeToggleItem('income', newType === 'income')}
                  >
                    {t('type_income')}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_amount')}</label>
                <input
                  type="number"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  placeholder={t('recurring_form_amount_placeholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_category')}</label>
                <CategoryCombobox
                  categories={categories}
                  value={newCategoryId}
                  onChange={setNewCategoryId}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_account')}</label>
                <select
                  value={newAccountId}
                  onChange={(e) => setNewAccountId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_pattern')}</label>
                <select
                  value={newPattern}
                  onChange={(e) => setNewPattern(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                >
                  {PATTERN_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{t(p.labelKey)}</option>
                  ))}
                </select>
              </div>
              {(newPattern === 'monthly_fixed' || newPattern === 'yearly') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_day_of_month')}</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={newDayOfMonth}
                    onChange={(e) => setNewDayOfMonth(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  />
                </div>
              )}
              {newPattern === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_day_of_week')}</label>
                  <select
                    value={newDayOfWeek}
                    onChange={(e) => setNewDayOfWeek(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  >
                    {DAY_OF_WEEK_KEYS.map((key, idx) => (
                      <option key={idx} value={idx}>{t(key)}</option>
                    ))}
                  </select>
                </div>
              )}
              {newPattern === 'yearly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('recurring_form_month')}</label>
                  <select
                    value={newMonthOfYear}
                    onChange={(e) => setNewMonthOfYear(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>{t('common_month_n').replace('{n}', String(m))}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="reminderEnabled"
                  checked={newReminderEnabled}
                  onChange={(e) => setNewReminderEnabled(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded dark:border-gray-600"
                />
                <label htmlFor="reminderEnabled" className="text-sm text-gray-700 dark:text-gray-300">{t('recurring_form_reminder')}</label>
              </div>
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="recurringExcluded"
                  checked={newExcluded}
                  onChange={(e) => setNewExcluded(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded dark:border-gray-600"
                />
                <label htmlFor="recurringExcluded" className="text-sm text-gray-700 dark:text-gray-300">
                  {t('recurring_form_excluded')}
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{t('recurring_form_excluded_hint')}</span>
                </label>
              </div>
            </div>
            {modalError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{modalError}</p>}
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className={btnSecondary}
              >
                {t('common_cancel')}
              </button>
              <button
                onClick={() => { void handleModalSave() }}
                disabled={modalSaving}
                className={btnPrimary}
              >
                {modalSaving ? t('common_saving') : t('common_save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
